import { useCallback, useReducer, useRef } from "react";

const DEFAULT_LIMIT = 50;

// ---------------------------------------------------------------------------
// editorKit/useEditorHistory — undo/redo générique pour un éditeur visuel.
//
// Calqué sur le pattern déjà éprouvé dans EmailPageBuilder.jsx (pile de
// snapshots bornée, rejoue sans se ré-enregistrer elle-même), généralisé
// pour n'importe quel état contrôlé (le `doc` de pageBuilder, mais aussi
// réutilisable tel quel par les deux autres éditeurs).
//
// Usage :
//   const history = useEditorHistory(doc, setDoc);
//   history.commit((prev) => nextDoc)   // action instantanée (delete, style…)
//   history.beginGesture()              // au pointerdown d'un drag/resize
//   history.updateGesture((prev) => …)  // à chaque pointermove (pas d'entrée d'historique)
//   history.endGesture()                // au pointerup — UNE SEULE entrée pour tout le geste
//   history.undo() / history.redo()
//
// `commit`/`updateGesture` acceptent soit une valeur, soit un updater
// (prevState) => nextState, comme le setState de React.
// ---------------------------------------------------------------------------
export function useEditorHistory(state, setState, { limit = DEFAULT_LIMIT } = {}) {
  const past = useRef([]);
  const future = useRef([]);
  const gestureStart = useRef(null);
  const stateRef = useRef(state);
  stateRef.current = state;
  const [, bump] = useReducer((c) => c + 1, 0);

  const apply = useCallback((updater) => {
    setState((prev) => (typeof updater === "function" ? updater(prev) : updater));
  }, [setState]);

  // Action instantanée : enregistre l'état précédent puis applique le changement.
  // Si un geste est en cours (beginGesture déjà appelé), ne double pas
  // l'entrée d'historique — le geste se chargera de l'enregistrer à sa fin.
  const commit = useCallback((updater) => {
    // NB: on teste `=== null` (pas la simple vérité de gestureStart.current) —
    // un geste peut légitimement capturer une valeur "fausse" (0, false, "")
    // comme état de départ, qu'il ne faut pas confondre avec "aucun geste
    // en cours". Le sentinel est le wrapper {value} lui-même, jamais falsy.
    if (gestureStart.current === null) {
      past.current = [...past.current, stateRef.current].slice(-limit);
      future.current = [];
    }
    apply(updater);
    bump();
  }, [apply, limit]);

  const beginGesture = useCallback(() => {
    if (gestureStart.current !== null) return;
    gestureStart.current = { value: stateRef.current };
  }, []);

  // Pendant le geste (mousemove) : applique le changement SANS créer d'entrée
  // d'historique — évite qu'un drag de 200 mouvements de souris consomme
  // 200 crans d'undo.
  const updateGesture = useCallback((updater) => {
    apply(updater);
  }, [apply]);

  const endGesture = useCallback(() => {
    if (gestureStart.current === null) return;
    past.current = [...past.current, gestureStart.current.value].slice(-limit);
    future.current = [];
    gestureStart.current = null;
    bump();
  }, [limit]);

  // Annule un geste en cours sans créer d'entrée d'historique (ex: Escape
  // pendant un drag) — laisse l'état tel qu'il est actuellement, ne
  // revient pas en arrière automatiquement (l'appelant peut le faire via
  // apply(gestureStart.current) s'il le souhaite avant d'appeler ceci).
  const cancelGesture = useCallback(() => {
    gestureStart.current = null;
  }, []);

  const undo = useCallback(() => {
    if (past.current.length === 0) return false;
    const previous = past.current[past.current.length - 1];
    past.current = past.current.slice(0, -1);
    future.current = [stateRef.current, ...future.current].slice(0, limit);
    setState(previous);
    bump();
    return true;
  }, [limit, setState]);

  const redo = useCallback(() => {
    if (future.current.length === 0) return false;
    const next = future.current[0];
    future.current = future.current.slice(1);
    past.current = [...past.current, stateRef.current].slice(-limit);
    setState(next);
    bump();
    return true;
  }, [limit, setState]);

  const isGestureActive = useCallback(() => gestureStart.current !== null, []);

  // Oublie tout l'historique (ex: après un enregistrement qui remplace l'état
  // par la version renvoyée par le serveur).
  const reset = useCallback(() => {
    past.current = [];
    future.current = [];
    gestureStart.current = null;
    bump();
  }, []);

  return {
    commit,
    beginGesture,
    updateGesture,
    endGesture,
    cancelGesture,
    isGestureActive,
    reset,
    undo,
    redo,
    canUndo: past.current.length > 0,
    canRedo: future.current.length > 0,
  };
}
